import { useEffect } from "react";
import PHFHero from "../shared/PHFHero";
import PHFIntro from "../shared/PHFIntro";
import PHFFeatures from "../shared/PHFFeatures";
import PHFHowItWorks from "../shared/PHFHowItWorks";
import PHFSplitImage from "../shared/PHFSplitImage";
import PHFCTA from "../shared/PHFCTA";

const veteransHero = {
  sm: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-veterans-hero-480.webp",
  md: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-veterans-hero-1280.webp",
  lg: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-veterans-hero-1980.webp",
  src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-veterans-hero-1280.webp",
};

const VeteransPage = () => {
  useEffect(() => {
    document.title = "Battle Buddies – Free Companion Animals for Veterans | Sponsor a Pet";
  }, []);

  return (
    <main>
      <PHFHero
        image={veteransHero}
        title="A steady companion can help you find your footing again"
        subtext="Battle Buddies connects eligible veterans with a free, carefully matched companion animal—so support can start now."
        buttonText="Apply for a Battle Buddy"
        buttonUrl="https://purpleheartfoundation.org/battle-buddies/"
        disclaimer="Companion animals / emotional support animals. Not service animals."
      />
      <section aria-label="Introduction">
      <PHFIntro
        title="You don't need to explain why this feels hard"
        body="If things feel heavier than they used to, you're not alone. Many veterans experience isolation, disrupted routines, & transition stress after service. A companion animal can help rebuild a daily rhythm—small, steady steps that support grounding, connection, & emotional stability over time."
      />
      </section>
      <section aria-label="What Battle Buddies provides">
      <PHFFeatures
        image="https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-cat.png"
        title="What Battle Buddies provides"
        subtext="This program is designed to remove common barriers to adoption while prioritizing safe, thoughtful placements."
        items={[
          "A free companion animal from a trusted rescue partner",
          "Thoughtful matching based on temperament, lifestyle, & household needs",
          "Adoption fees & initial medical costs covered",
          "Early post-adoption check-ins to support a stable transition",
        ]}
      />
      </section>

      {/* What's Covered Section */}
      <section aria-label="What's covered">
      <div className="bg-blue-light1 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="!text-[36px] font-bold text-blue-navy font-poppins text-left mb-10">
            What's covered
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Included */}
            <div className="bg-white rounded-2xl border border-blue p-8">
              <h3 className="!text-[24px] font-bold text-blue font-poppins text-center mb-6">
                Included
              </h3>
              <ul className="space-y-4">
                {["Adoption fee", "Spay/neuter", "Initial vet exam", "Core vaccines & testing", "Microchip"].map((item) => (
                  <li key={item} className="flex items-center gap-4">
                    <svg className="w-5 h-5 text-blue flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-blue-navy text-[16px]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Not Included */}
            <div className="bg-white rounded-2xl border border-orange p-8">
              <h3 className="!text-[24px] font-bold text-orange font-poppins text-center mb-6">
                Not included
              </h3>
              <ul className="space-y-4">
                {["Ongoing food", "Routine care", "Emergency vet costs after placement"].map((item) => (
                  <li key={item} className="flex items-center gap-4">
                    <span className="text-orange text-xl font-bold flex-shrink-0">—</span>
                    <span className="text-blue-navy text-[16px]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      </section>

      <section aria-label="How it works">
      <PHFHowItWorks
        bgColor="bg-blue-navy"
        title="How it works"
        steps={[
          {
            title: "Apply & share your preferences",
            description: "Tell us about your lifestyle, housing, & what you're looking for in a companion.",
          },
          {
            title: "Meet a matched companion",
            description: "Rescue partners help identify animals suited for calm, supportive companionship.",
          },
          {
            title: "Adoption costs are covered",
            description: "Eligible upfront costs are funded so finances aren't the deciding factor.",
          },
          {
            title: "Supported check-ins after placement",
            description: "Early follow-ups help ensure both you & your companion are adjusting well.",
          },
        ]}
      />
      </section>
      <section aria-label="Who can apply">
      <PHFSplitImage
        image="https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-dog.png"
        title="Who can apply"
        body="To apply, veterans must meet a small set of eligibility criteria designed to ensure safe, sustainable placements for both people & animals."
        items={[
          "Honorably discharged or combat-wounded veterans",
          "Demonstrated need (PTSD, TBI, anxiety, transition stress)",
          "Stable, pet-permitted housing",
        ]}
        disclaimer="Eligibility determined by the Purple Heart Foundation"
        imageLeft={false}
      />
      </section>
      <section aria-label="Apply now">
      <PHFCTA
        title="Ready to meet your Battle Buddy?"
        body="We'll guide you through each step of the process — from application to placement, with support along the way."
        highlight="You're not doing this alone."
        buttonText="Apply now"
        buttonUrl="https://purpleheartfoundation.org/battle-buddies/"
      />
      </section>
    </main>
  );
};

export default VeteransPage;
